import React, { useEffect, useRef, useState } from 'react';
import { Badge } from './ui/badge';

const CategoryRow = ({ category, technologies }) => {
  const [isVisible, setIsVisible] = useState(false);
  const rowRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // THE MAGIC LINE: This toggles the animation ON when scrolling in, and OFF when scrolling out!
          setIsVisible(entry.isIntersecting);
        });
      },
      { rootMargin: "-50px 0px" }
    );

    if (rowRef.current) {
      observer.observe(rowRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={rowRef} className="mb-16 last:mb-0">
      {/* Category Title */}
      <h3
        className={`text-2xl font-bold text-gray-800 mb-6 border-b-2 border-gray-200 pb-2 inline-block transition-all duration-1000 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}
      >
        {category}
      </h3>

      <div className="flex flex-wrap gap-4">
        {technologies
          .filter(tech => tech.category === category)
          .map((tech, techIndex) => (
            <div
              key={tech.name}
              className={`transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-90'
                }`}
              style={{ transitionDelay: `${(techIndex + 1) * 100}ms` }}
            >
              <Badge
                className="px-6 py-3 text-base bg-white border border-gray-200 text-gray-600 hover:border-[#2d4f4f] hover:text-[#2d4f4f] hover:bg-white hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 cursor-default"
              >
                {tech.name}
              </Badge>
            </div>
          ))}
      </div>
    </div>
  );
};


const TechnologiesSection = ({ technologies }) => {
  const [isHeaderVisible, setIsHeaderVisible] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // We also apply the magic two-way line to the main header!
          setIsHeaderVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) {
      observer.observe(headerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const categories = [...new Set(technologies.map(tech => tech.category))];

  return (
    <section id="technologies" className="py-24 lg:py-32 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-20 transition-all duration-1000 ease-out ${isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            The <span className="text-[#2d4f4f]">Tech Stack</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Working with modern, proven technologies to build fast, scalable, and maintainable solutions.
          </p>
        </div>

        {/* Technologies by Category */}
        <div>
          {categories.map((category) => (
            <CategoryRow
              key={category}
              category={category}
              technologies={technologies}
            />
          ))}
        </div>

      </div>
    </section>
  );
};

export default TechnologiesSection;